/**
 * What the Redux store may tell Sentry (REF-6708 privacy slice A).
 *
 * `Sentry.createReduxEnhancer` sends three things on every captured error, message and transaction:
 * the state (as `contexts.state`), every dispatched action (as `redux.action` breadcrumbs), and, by
 * default, a full-depth `redux_state.json` attachment. The previous spread-and-redact transformer
 * filtered `user.email` and passed everything else — including the Browzwear `apiKey`/`apiSecret`
 * that `setBrowzwear` puts in state, in every later event, for the rest of the session.
 *
 * This module is an ALLOWLIST: each output object is built field by field, every value is validated
 * at runtime (Redux state is not type-checked), and anything not listed — a new field, a new slice, a
 * free-text string, an id, a URL — is dropped by construction. Rules:
 *   - booleans only when `typeof === "boolean"`, numbers only when finite;
 *   - strings only from a literal set (else "other") or a strict id format (else null);
 *   - free text, URLs, names, emails and credentials become a count / boolean, or are dropped.
 *
 * Adding a slice or a page-state field: classify it here. `sentryRedux.test.ts` fails until you do.
 * Spec: crew report `privacy-slice-A-spec.md` §2–§3, with the user decisions of 2026-10-08.
 */
import type { ShootingType, PoseMode } from "pages/PhotoshootStudioPage/PhotoshootStudioContext";
import type { GuidanceBlockedReason, GuidanceStep, ExtractionOutcome } from "pages/TechPackPage/utils/guidanceReport";
import type { SidebarPanel } from "pages/DesignStudioPage/components/canvas/context/layoutContexts";
import type { ModuleKey, PackageCode } from "types/permissions";
import type { GuideSource } from "store/guidanceSlice";
import type { ChatStatus } from "store/chatbotSlice";
import { GENERATION_RATIOS } from "pages/DesignStudioPage/components/canvas/utils/canvasSizeConstants";
import { TECHPACK_CREATE_FLOW_ID } from "pages/TechPackPage/utils/techpackCreateFlow";
import { SUPPORTED_LOCALES } from "i18n/config";
import { ScreenType } from "components/WarningSnackbar/WarningSnackbar.types";
import { AccountSelectMenu } from "pages/Account/accountMenu";
import { commonSlice } from "./commonSlice";
import { historySlice } from "./historySlice";
import { designSlice } from "./designSlice";
import { snackbarSlice } from "./snackbarSlice";
import { creditSlice } from "./creditSlice";
import { whatsNewSlice } from "./whatsNewSlice";
import { chatbotSlice } from "./chatbotSlice";
import { guidanceSlice } from "./guidanceSlice";
import { notificationsSlice } from "./notificationsSlice";

type Unknown = Record<string, unknown>;

// ---- validators ---------------------------------------------------------------------------------

const asObject = (value: unknown): Unknown | null => (value !== null && typeof value === "object" && !Array.isArray(value) ? (value as Unknown) : null);
const bool = (value: unknown): boolean | null => (typeof value === "boolean" ? value : null);
const num = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);
const int = (value: unknown): number | null => (Number.isInteger(value) ? (value as number) : null);
const MAX_ARRAY_LENGTH = 2 ** 32 - 1;
// Every validator reads its input ONCE and returns a value it checked itself: state is plain data,
// but a getter, proxy or overridden method must not be able to hand back something else on a 2nd read.
const count = (value: unknown): number | null => {
  if (!Array.isArray(value)) return null;
  const length: unknown = value.length;

  // A real array length is an integer in 0..2^32-1; anything else (a proxy's 2^54) is not trusted.
  return Number.isInteger(length) && (length as number) >= 0 && (length as number) <= MAX_ARRAY_LENGTH ? (length as number) : null;
};
const isBool = (value: unknown): boolean => typeof value === "boolean";
const isFiniteInt = (value: unknown): boolean => Number.isInteger(value);

/** `"other"` for a present value outside the set; `null` stays `null` (and `undefined` becomes `null`). */
const oneOf =
  <T extends string | number>(values: ReadonlySet<T>) =>
  (value: unknown): T | "other" | null => {
    if (value === null || value === undefined) return null;

    return values.has(value as T) ? (value as T) : "other";
  };
/** Like `oneOf`, for numeric enums: anything outside the set is `null` (a number has no "other"). */
const numericOneOf =
  (values: ReadonlySet<number>) =>
  (value: unknown): number | null =>
    typeof value === "number" && values.has(value) ? value : null;
/** A string union, pinned by tsc: the Record must list every member of `T` and nothing else. */
const literals = <T extends string>(table: Record<T, true>): ReadonlySet<T> => new Set(Object.keys(table) as T[]);
const numericEnumValues = (enumObject: Record<string, string | number>): ReadonlySet<number> => new Set(Object.values(enumObject).filter((v): v is number => typeof v === "number"));

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const uuid = (value: unknown): string | null => (typeof value === "string" && UUID.test(value) ? value : null);

// ---- literal sets -------------------------------------------------------------------------------

const PACKAGE_CODE = oneOf(literals<PackageCode>({ design_starter: true, photoshoot_starter: true, design_pro: true, design_studio: true, creative_suite: true, prod_fake_enterprice: true }));
const MODULE_KEY_TABLE: Record<ModuleKey, true> = {
  design: true,
  photoshoot: true,
  model_training: true,
  defacto_roar: true,
  artistic_sketch: true,
  "3d_measurement": true,
  trends: true,
  brand_catalogue: true,
  admin: true,
  export_to_joor: true,
  collection_range: true,
  api_usage: true,
  agent_chat: true,
};
const MODULE_KEYS = Object.keys(MODULE_KEY_TABLE) as ModuleKey[];
const LOCALE = oneOf(new Set<string>(SUPPORTED_LOCALES));
const TOP_BAR_MODE = oneOf(new Set(["full", "agent", "hidden"]));
const ACCOUNT_MENU = numericOneOf(numericEnumValues(AccountSelectMenu));
// `TutorialType` (components/Tutorial/components/TutorialItem.tsx): Design, Train, EditImage. Not
// imported — that module is a component that reaches the store.
const TUTORIAL_TYPE = numericOneOf(new Set([0, 1, 2]));
const SCREEN = numericOneOf(numericEnumValues(ScreenType));
// The only statuses Router's EventDispatchSnackbar bridge forwards into the slice (Router.tsx handleEvent).
const SNACKBAR_TYPE = numericOneOf(new Set([402, 409, 503]));
// HttpClient's 402 branch sets `panel = "banner"` for the mannequin banner, else "".
const SNACKBAR_PANEL = oneOf(new Set(["", "banner"]));
const MESSAGE_TYPE = oneOf(new Set(["popup", "message", ""]));
const POPUP_REASON = oneOf(new Set(["credits", "permissions", ""]));
// Every popup type WarningSnackbar acts on (WarningSnackbar.utils.ts + WarningSnackbar.tsx), plus the
// slice default "". The value is backend-controlled (HttpClient forwards `popup_type`), so anything
// else — a new BE value, free text — is "other".
const POPUP_TYPE = oneOf(
  new Set([
    "",
    "design_starter",
    "photoshoot_starter",
    "design_pro",
    "design_studio",
    "creative_suite",
    "contact_form",
    "contact_us",
    "end_trial",
    "past_due",
    "contact_account_manager",
    "member_credit_limit",
  ])
);
const CHAT_ROLE = oneOf(new Set(["user", "assistant"]));
const CHAT_STATUS = oneOf(literals<ChatStatus>({ streaming: true, done: true, canceled: true, error: true, interrupted: true }));
const GUIDANCE_STEPS = literals<GuidanceStep>({ images: true, review: true, extracting: true, done: true });
const GUIDANCE_BLOCKED = literals<GuidanceBlockedReason>({
  images_missing: true,
  images_analyzing: true,
  images_failed: true,
  images_incomplete: true,
  images_unconfirmed: true,
});
const GUIDE_STEP = oneOf(GUIDANCE_STEPS);
const GUIDE_BLOCKED = oneOf(GUIDANCE_BLOCKED);
const GUIDE_SOURCE = oneOf(literals<GuideSource>({ url_param: true, chatbot: true }));
const GUIDE_STATUS = oneOf(new Set(["active", "completed"]));
// Features that publish a guidance report (`useGuidanceReporter("tech_pack", …)` in TechPackProvider).
const GUIDE_FEATURE = oneOf(new Set(["tech_pack"]));
// Registered guided flows (hooks/useGuideCoordinator FLOWS).
const GUIDE_FLOW = oneOf(new Set([TECHPACK_CREATE_FLOW_ID]));

// ---- page-state snapshot ------------------------------------------------------------------------

type FieldValidator = (value: unknown) => unknown;
const B: FieldValidator = bool;
const N: FieldValidator = num;
const E = (values: ReadonlySet<string>): FieldValidator => oneOf(values);

const SHOOTING = new Set<string>([...literals<NonNullable<ShootingType>>({ flat: true, mannequin: true, full_look: true, ghost: true, background_change: true }), "unset"]);

/**
 * Every field each `usePageStateReporter(page, …)` call emits, with its validator. Keys and values are
 * both enumerated: the snapshot type is `Record<string, PageStateValue>`, which validates nothing.
 */
export const PAGE_STATE_FIELDS: Record<string, Record<string, FieldValidator>> = {
  // pages/DesignStudioPage/components/layout/CanvasPageStateReporter.tsx
  design_canvas: {
    hasProject: B,
    isTemplate: B,
    hasLoadError: B,
    nodeCount: N,
    assetNodeCount: N,
    selectedNodeCount: N,
    imageCount: N,
    remainingImageSlots: N,
    activeGenerationCount: N,
    isGenerating: B,
    // FloatingGenerationPanel.tsx `MODES` (not exported).
    generationMode: E(new Set(["New Style", "New Pattern", "New Moodboard"])),
    aspectRatio: E(new Set(GENERATION_RATIOS)),
    // FloatingGenerationPanel.tsx resolution picker `["2K", "4K"]` (not exported).
    resolution: E(new Set(["2K", "4K"])),
    outputCount: N,
    referenceCount: N,
    hasPrompt: B,
    hasBrandDna: B,
    openPanel: E(
      new Set([
        ...literals<SidebarPanel>({
          assetsV2: true,
          trends: true,
          fabrics: true,
          colorPalettes: true,
          history: true,
          help: true,
          promptAssistant: true,
          moodboards: true,
          brandKits: true,
          collections: true,
        }),
        "none",
      ])
    ),
    isTrendAgentOpen: B,
    isEditorOpen: B,
    isCropping: B,
  },
  // pages/PhotoshootStudioPage/PhotoshootStudioContext.tsx
  photoshoot_studio: {
    shootingType: E(SHOOTING),
    poseMode: E(new Set([...literals<PoseMode>({ keep: true, select: true }), "n/a"])),
    effectiveMode: E(SHOOTING),
    mainProductCount: N,
    combinationRowCount: N,
    combinationsFilledCount: N,
    hasAnyComplementary: B,
    canContinue: B,
  },
  // pages/FabricTrainingPage/FabricTrainingProvider.tsx — `fabricType` is a title (free text) there.
  fabric_training: {
    fabricType: (value) => (value === "unset" ? "unset" : typeof value === "string" ? "set" : null),
    swatchCount: N,
    hasGsm: B,
    hasComposition: B,
    hasPattern: B,
    hasColorCodes: B,
    hasTrainId: B,
    isFabricAnalysisStarted: B,
  },
  // pages/StyleTrainingPage/StyleTrainingProvider.tsx — no production write sets a class today.
  style_training: {
    hasTrainId: B,
    uploadedImageCount: N,
    trainingClass: E(new Set(["unset"])),
    hasTrainInfo: B,
    isFetchingTrainInfo: B,
  },
  // pages/TechPackPage/TechPackProvider.tsx
  tech_pack: {
    hasProjectId: B,
    hasFrontImage: B,
    hasBackImage: B,
    hasSideImage: B,
    hasFrontDrawing: B,
    hasBackDrawing: B,
    hasSideDrawing: B,
    hasBodyPreset: B,
    extractionStatus: E(new Set(["processing", "complete", "idle"])),
    isGeneratingTechpackInfo: B,
    hasTechpackInfo: B,
    has3dGarment: B,
    currentStep: N,
    guideStep: E(GUIDANCE_STEPS),
    guideBlocked: E(new Set([...GUIDANCE_BLOCKED, "none"])),
    guideNotice: E(new Set([...literals<ExtractionOutcome>({ failed: true, timeout: true }), "none"])),
  },
  // pages/SvgEditorPage/SvgEditorProvider.tsx
  svg_editor: {
    svgLoaded: B,
    hasUnsavedEdits: B,
    canExportSvg: B,
    hasSaveButton: B,
    editsPersist: B,
  },
  // pages/ModelTrainingPage/ModelTrainingProvider.tsx
  model_training: {
    hasTrainId: B,
    hasModelId: B,
    isModelDraftCreated: B,
    hasModelPreview: B,
    isNewModelFlow: B,
    selectedPoseCount: N,
    selectedStyleCount: N,
    hasModelConfig: B,
    isFetchingModelImages: B,
    isGeneratingAssetPreview: B,
    hasTrainInfo: B,
  },
};

const sanitizePageState = (value: unknown) => {
  const snapshot = asObject(value);
  if (!snapshot) return null;

  const rawPage = snapshot.page;
  const page = typeof rawPage === "string" && Object.prototype.hasOwnProperty.call(PAGE_STATE_FIELDS, rawPage) ? rawPage : null;
  const sourceFields = asObject(snapshot.fields) ?? {};
  const fields: Unknown = {};
  if (page) {
    Object.entries(PAGE_STATE_FIELDS[page]).forEach(([key, validate]) => {
      if (Object.prototype.hasOwnProperty.call(sourceFields, key)) fields[key] = validate(sourceFields[key]);
    });
  }

  return { page: page ?? "other", fields };
};

// ---- slices -------------------------------------------------------------------------------------

/** Chat messages are user questions and model output: only their shape is kept, for the last 20. */
const CHAT_MESSAGE_LIMIT = 20;

/** `{<allowlisted module>: boolean}` for one flag; unknown modules and non-boolean flags are dropped. */
const permissionFlags = (permissions: Unknown | null, flag: "is_enabled" | "is_visible") =>
  permissions
    ? Object.fromEntries(
        MODULE_KEYS.flatMap((key) => {
          const value = asObject(permissions[key])?.[flag];

          return typeof value === "boolean" ? [[key, value]] : [];
        })
      )
    : null;

const sanitizeCommon = (value: unknown) => {
  const common = asObject(value) ?? {};
  const user = asObject(common.user) ?? {};
  const permissions = asObject(user.permissions);
  const demoInfo = asObject(user.demoInfo);
  const searchInput = asObject(common.searchInput) ?? {};
  const tutorial = asObject(common.tutorial) ?? {};
  const credits = asObject(common.credits) ?? {};
  const creditPool = (pool: unknown) => {
    const p = asObject(pool) ?? {};

    return { total: num(p.total), used: num(p.used) };
  };
  const nonEmptyString = (v: unknown) => (typeof v === "string" ? v.length > 0 : null);

  return {
    isAuthorized: bool(common.isAuthorized),
    isCheckingToken: bool(common.isCheckingToken),
    permissionsError: bool(common.permissionsError),
    isChatbotOpen: bool(common.isChatbotOpen),
    topBarMode: TOP_BAR_MODE(common.topBarMode),
    isSidebarCollapsed: bool(common.isSidebarCollapsed),
    isMainMenuFixed: bool(common.isMainMenuFixed),
    showSearchBar: bool(common.showSearchBar),
    showImagePopup: bool(common.showImagePopup),
    imagePopupOpen: bool(common.showImagePopup),
    showEditProfileModal: bool(common.showEditProfileModal),
    showChangePasswordModal: bool(common.showChangePasswordModal),
    showDeleteAccountModal: bool(common.showDeleteAccountModal),
    showUserFlow: bool(common.showUserFlow),
    showUpdateModal: bool(common.showUpdateModal),
    showFeatureAnnouncementPopup: bool(common.showFeatureAnnouncementPopup),
    showWebinarModal: bool(common.showWebinarModal),
    noReset: bool(common.noReset),
    accountSelectedMenu: ACCOUNT_MENU(common.accountSelectedMenu),
    isDesignTutorialCompleted: bool(common.isDesignTutorialCompleted),
    isEditTutorialCompleted: bool(common.isEditTutorialCompleted),
    isTrainTutorialCompleted: bool(common.isTrainTutorialCompleted),
    browzwearConnected: bool(asObject(common.browzwear)?.isConnected),
    user: {
      hasUserId: user.id !== undefined && user.id !== null && user.id !== "",
      // "" / undefined is "not loaded yet", not an unknown plan.
      packageCode: user.packageCode === "" ? null : PACKAGE_CODE(user.packageCode),
      language: LOCALE(user.language),
      privacy_perm: num(user.privacy_perm),
      privateSession: num(user.privateSession),
      createPeriod: num(user.createPeriod),
      // Flat module → boolean maps: a nested `{module: {is_enabled}}` sits one level past Sentry's
      // default normalisation depth for `contexts.state` and arrives as "[Object]".
      permissionsEnabled: permissionFlags(permissions, "is_enabled"),
      permissionsVisible: permissionFlags(permissions, "is_visible"),
      demoInfo: demoInfo
        ? {
            is_design: bool(demoInfo.is_design),
            is_mannequin: bool(demoInfo.is_mannequin),
            is_video: bool(demoInfo.is_video),
            is_techpack: bool(demoInfo.is_techpack),
            is_trend: bool(demoInfo.is_trend),
          }
        : null,
      notifInfoCount: count(user.notifInfo),
    },
    hasSearchText: nonEmptyString(searchInput.text),
    hasSearchImage: nonEmptyString(searchInput.image),
    snackbarCount: count(common.snackbar),
    tutorial: { show: bool(tutorial.show), type: TUTORIAL_TYPE(tutorial.type) },
    currentPageState: sanitizePageState(common.currentPageState),
    pendingMoodboardCount: count(common.pendingMoodboards),
    credits: {
      loaded: bool(credits.loaded),
      design: creditPool(credits.design),
      designBonus: num(credits.designBonus),
      photoshoot: creditPool(credits.photoshoot),
    },
  };
};

const sanitizeSnackbar = (value: unknown) => {
  const snackbar = asObject(value) ?? {};

  return {
    visible: bool(snackbar.visible),
    type: SNACKBAR_TYPE(snackbar.type),
    screen: SCREEN(snackbar.screen),
    panel: SNACKBAR_PANEL(snackbar.panel),
    messageType: MESSAGE_TYPE(snackbar.messageType),
    popupType: POPUP_TYPE(snackbar.popupType),
    popupReason: POPUP_REASON(snackbar.popupReason),
    requiredCredits: num(snackbar.requiredCredits),
    remainingCredits: num(snackbar.remainingCredits),
  };
};

const sanitizeWhatsNew = (value: unknown) => {
  const whatsNew = asObject(value) ?? {};
  const active = asObject(whatsNew.activeTourItem);
  const tours = asObject(whatsNew.tours);

  return {
    itemCount: count(whatsNew.items),
    unreadCount: num(whatsNew.unreadCount),
    loaded: bool(whatsNew.loaded),
    locale: LOCALE(whatsNew.locale),
    tourCount: tours ? Object.keys(tours).length : null,
    activeTourOpen: active !== null,
    // A What's New item id is a backend string of unknown shape: kept only as a UUID or an integer.
    activeTourItemId: active ? uuid(active.id) ?? int(active.id) : null,
  };
};

const sanitizeChatbot = (value: unknown) => {
  const chatbot = asObject(value) ?? {};
  // Indexed reads only — never the source array's own `slice`/`map`, which an override could replace.
  const source = chatbot.messages;
  const total = count(source) ?? 0;
  const kept = Math.min(total, CHAT_MESSAGE_LIMIT);
  const messages: unknown[] = [];
  for (let k = 0; k < kept; k += 1) messages.push((source as unknown[])[total - kept + k]);

  return {
    messages: messages.map((raw) => {
      const message = asObject(raw) ?? {};
      const isIntro = message.isIntro === true;
      const text = message.text;

      return {
        // Session/message ids are client-generated UUIDs kept on purpose to match BE chatbot logs.
        id: isIntro ? "intro" : uuid(message.id),
        role: CHAT_ROLE(message.role),
        status: CHAT_STATUS(message.status),
        sessionId: uuid(message.sessionId),
        isIntro,
        textLength: typeof text === "string" ? text.length : 0,
        actionCount: count(message.actions) ?? 0,
      };
    }),
    activeSessionId: uuid(chatbot.activeSessionId),
    lastChunkIndex: int(chatbot.lastChunkIndex),
  };
};

const sanitizeGuidance = (value: unknown) => {
  const guidance = asObject(value) ?? {};
  const published = asObject(guidance.published);
  const report = asObject(published?.report);
  const active = asObject(guidance.active);

  return {
    published: published
      ? {
          feature: GUIDE_FEATURE(published.feature),
          report: report ? { step: GUIDE_STEP(report.step), stepReady: bool(report.stepReady), blockedReason: GUIDE_BLOCKED(report.blockedReason) } : null,
        }
      : null,
    active: active
      ? {
          flowId: GUIDE_FLOW(active.flowId),
          version: num(active.version),
          source: GUIDE_SOURCE(active.source),
          status: GUIDE_STATUS(active.status),
          lastEmittedStep: GUIDE_STEP(active.lastEmittedStep),
          lastEmittedBlockedReason: GUIDE_BLOCKED(active.lastEmittedBlockedReason),
        }
      : null,
  };
};

const sanitizeNotifications = (value: unknown) => {
  const notifications = asObject(value) ?? {};

  return {
    itemCount: count(notifications.items),
    toastCount: count(notifications.toasts),
    unreadCount: num(notifications.unreadCount),
    page: num(notifications.page),
    hasMore: bool(notifications.hasMore),
    loaded: bool(notifications.loaded),
  };
};

/** The slices `sanitizeReduxState` classifies, in store order. A slice not listed here is not sent. */
export const SANITIZED_SLICES = ["common", "history", "design", "snackbar", "credit", "whatsNew", "chatbot", "guidance", "notifications", "siteImages", "reports"] as const;

/**
 * The Redux state Sentry receives. Never the live state: a fresh object, every field validated.
 * Never throws — it runs inside every reducer call, and a throw there would break `dispatch`.
 */
export const sanitizeReduxState = (state: unknown) => {
  try {
    const root = asObject(state) ?? {};
    const credit = asObject(root.credit) ?? {};

    return {
      common: sanitizeCommon(root.common),
      history: { selectedItemCount: count(asObject(root.history)?.selectedItems) },
      design: { maskCount: count(asObject(root.design)?.masks) },
      snackbar: sanitizeSnackbar(root.snackbar),
      credit: { multiplierLoaded: credit.creditMultiplier != null, packageListLoaded: credit.packageList != null },
      whatsNew: sanitizeWhatsNew(root.whatsNew),
      chatbot: sanitizeChatbot(root.chatbot),
      guidance: sanitizeGuidance(root.guidance),
      notifications: sanitizeNotifications(root.notifications),
      // `siteImages` (site-images) holds backoffice asset URLs and preview-link state: registered, nothing of it is sent.
      // `reports` (REF-7271) holds the team's shared-report list: registered, nothing of it is sent.
      siteImages: {},
      reports: {},
    };
  } catch {
    return { sanitizeError: true };
  }
};

// ---- actions ------------------------------------------------------------------------------------

/** Every action type the app's slices define — derived, so it never needs hand maintenance. */
const KNOWN_ACTION_TYPES: ReadonlySet<string> = new Set(
  [commonSlice, historySlice, designSlice, snackbarSlice, creditSlice, whatsNewSlice, chatbotSlice, guidanceSlice, notificationsSlice].flatMap((slice) =>
    Object.values(slice.actions).map((creator) => creator.type)
  )
);

/**
 * The only actions whose payload is sent, each with its validator: a non-identifying boolean, integer
 * or enum. Any other payload — strings, ids (`setImagePopupItemId(12345)`), objects — never leaves.
 */
export const SCALAR_PAYLOAD_ACTIONS: Readonly<Record<string, (payload: unknown) => boolean>> = {
  [commonSlice.actions.setSidebarCollapsed.type]: isBool,
  [commonSlice.actions.setChatbotOpen.type]: isBool,
  [commonSlice.actions.setIsAuthorized.type]: isBool,
  [commonSlice.actions.setCheckingToken.type]: isBool,
  [commonSlice.actions.setPermissionsError.type]: isBool,
  [commonSlice.actions.setNoReset.type]: isBool,
  [commonSlice.actions.setTopBarMode.type]: (payload) => payload === null || payload === "full" || payload === "agent" || payload === "hidden",
  [notificationsSlice.actions.setUnreadCount.type]: isFiniteInt,
  [whatsNewSlice.actions.setWhatsNewUnreadCount.type]: isFiniteInt,
  [whatsNewSlice.actions.setWhatsNewLoaded.type]: isBool,
};

/** The `redux.action` breadcrumb: a known type (or a fixed label) and, at most, a validated scalar. */
export const sanitizeReduxAction = (action: unknown) => {
  try {
    const rawType = asObject(action)?.type;
    const type = typeof rawType === "string" && KNOWN_ACTION_TYPES.has(rawType) ? rawType : typeof rawType === "string" && rawType.startsWith("@@redux/") ? "redux/internal" : "redux/other";
    const payload = (action as Unknown).payload;
    const keepPayload = Object.prototype.hasOwnProperty.call(SCALAR_PAYLOAD_ACTIONS, type) && SCALAR_PAYLOAD_ACTIONS[type](payload);

    return keepPayload ? { type, payload } : { type };
  } catch {
    return { type: "redux/other" };
  }
};

export const sentryReduxEnhancerOptions = {
  stateTransformer: sanitizeReduxState,
  actionTransformer: sanitizeReduxAction,
  // The attachment is a full-depth JSON copy of the state context, built before `beforeSend`. With an
  // allowlisted context it adds nothing but risk and attachment quota.
  attachReduxState: false,
} as const;
